import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import Header from '../components/header';
import VizitStorageLoader from '../components/VizitStorageLoader';
import '../css/sidebar.css';
import { FaArrowRight } from 'react-icons/fa';
import { AiOutlineCloudUpload } from 'react-icons/ai';
import logo from '../img/logo-softmining.svg';
import { useAuth } from "../context/AuthContext";

import {
  handleFileUpload,
  exportVisibleNodes,      // unchanged
  exportSelectedNodes,     // unchanged: if >1 node selected, edges among them are included
  // showNodeDetails,       // REMOVED per client request
  showEdgeDetails,         // stays: shows edge details with relation title
  expandNode,
  setupGraph,              // signature unchanged: (jsonData, graphData, containerRef, onReady)
  updateNodeSize,
  updateLabelSize,
  filterGraph,
  selectAllNodes
} from '../Utility/Graph/graphUtils';

const sortByDateDesc = (arr) =>
  [...arr].sort((a, b) => {
    const ta = a?.date ? new Date(a.date).getTime() : 0;
    const tb = b?.date ? new Date(b.date).getTime() : 0;
    return tb - ta;
  });

const GraphComponent = () => {
  const containerRef = useRef(null);
  const [network, setNetwork] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const [file, setFile] = useState(null);
  const [jsonData, setJsonData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedCount, setSelectedCount] = useState(0);

  // Edge selection state
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [selectedEdgeCount, setSelectedEdgeCount] = useState(0);

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const { user } = useAuth();             
  const isAdmin = user?.data?.admin === true;

  // Normalize graph data
  const graphData = useMemo(() => {
    const base = jsonData ?? location.state?.params?.pipelineData ?? { nodes: [], edges: [] };
    if (!base) return { nodes: [], edges: [] };

    if (Array.isArray(base.nodes) && base.nodes.length > 0 && typeof base.nodes[0]?.id === 'undefined') {
      const transformedNodes = base.nodes.map(([label, group], index) => ({
        id: index + 1,
        label,
        group,
      }));

      const transformedEdges = (base.edges || [])
        .map(([[fromLabel, toLabel], value, additionalData], idx) => {
          const fromNode = transformedNodes.find(n => n.label === fromLabel);
          const toNode = transformedNodes.find(n => n.label === toLabel);
          if (!fromNode || !toNode) return null;
          return {
            id: `e_${fromNode.id}_${toNode.id}_${idx}`,
            from: fromNode.id,
            to: toNode.id,
            value,
            additionalData: Array.isArray(additionalData) ? additionalData : (additionalData ? [additionalData] : [])
          };
        })
        .filter(Boolean);

      return { nodes: transformedNodes, edges: transformedEdges };
    }

    if (!Array.isArray(base.nodes) || !Array.isArray(base.edges)) {
      console.error('Invalid graph data:', base);
      return { nodes: [], edges: [] };
    }
    const normalizedEdges = base.edges.map((e, idx) => ({
      id: e.id ?? `e_${e.from}_${e.to}_${idx}`,
      from: e.from,
      to: e.to,
      value: e.value ?? 0,
      additionalData: Array.isArray(e.additionalData) ? e.additionalData : (e.additionalData ? [e.additionalData] : [])
    }));
    return { nodes: base.nodes, edges: normalizedEdges };
  }, [jsonData, location.state?.params?.pipelineData]);

  // Init / redraw graph
  useEffect(() => {
    if (!graphData?.nodes?.length || !graphData?.edges?.length) {
      setIsLoading(false);
      return;
    }

    // Destroy previous network and clear container
    if (network?.destroy) {
      try { network.destroy(); } catch {}
      setNetwork(null);
    }
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
      containerRef.current.style.height = containerRef.current.style.height || '70vh';
      containerRef.current.style.width = containerRef.current.style.width || '100%';
    }

    let instance;
    const init = async () => {
      try {
        setIsLoading(true);

        const ready = setupGraph(
          jsonData,
          graphData,
          containerRef,
          (net) => {
            instance = net;
            setNetwork(net);

            // Sync selection on events
            const syncSelection = () => {
              const nodeIds = net.getSelectedNodes();
              const edgeIds = net.getSelectedEdges();
              setSelectedCount(nodeIds.length);
              setSelectedNode(nodeIds.length === 1 ? nodeIds[0] : null);
              setSelectedEdgeCount(edgeIds.length);
              setSelectedEdgeId(edgeIds.length === 1 ? edgeIds[0] : null);
            };

            net.on('click', syncSelection);
            net.on('selectNode', syncSelection);
            net.on('deselectNode', syncSelection);
            net.on('selectEdge', syncSelection);
            net.on('deselectEdge', syncSelection);
          }
        );

        if (!ready) {
          console.warn('setupGraph returned false (check signature or data).');
        }
      } catch (err) {
        console.error('Error during setupGraph:', err);
      } finally {
        setIsLoading(false);
      }
    };

    init();

    return () => {
      if (instance?.off) {
        instance.off('click');
        instance.off('selectNode');
        instance.off('deselectNode');
        instance.off('selectEdge');
        instance.off('deselectEdge');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphData, jsonData]);

  // Upload JSON
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      await handleFileUpload(e, setJsonData, setIsLoading);
    } catch (error) {
      console.error('Submit error:', error);
      setIsLoading(false);
    }
  };

  // Actions (right panel / context)
  const hideSelectedNodes = () => {
    if (!network) return;
    const nodeIds = network.getSelectedNodes();
    if (!nodeIds.length) return;
    nodeIds.forEach((id) => {
      network.body.data.nodes.update([{ id, hidden: true }]);
    });
    network.unselectAll();
    setSelectedCount(0);
    setSelectedNode(null);
    setSelectedEdgeCount(0);
    setSelectedEdgeId(null);
  };

  const handleExpandSelected = () => {
    if (!network) return;
    const nodeIds = network.getSelectedNodes();
    if (!nodeIds.length) return;
    nodeIds.forEach((id) => expandNode(graphData, id, network));
    network.unselectAll();
    setSelectedCount(0);
    setSelectedNode(null);
    setSelectedEdgeCount(0);
    setSelectedEdgeId(null);
  };

  const handleDownloadSelected = () => {
    if (!network) return;
    exportSelectedNodes(graphData, network); // includes edges among selected nodes
    network.unselectAll();
    setSelectedCount(0);
    setSelectedNode(null);
    setSelectedEdgeCount(0);
    setSelectedEdgeId(null);
  };

  const handleSelectAllNodes = () => {
    if (!network) return;
    selectAllNodes(network);
    const selectedIds = network.getSelectedNodes();
    setSelectedCount(selectedIds.length);
    setSelectedNode(selectedIds.length !== 1 ? null : selectedIds[0]);
  };

  const noGraphToShow = !graphData.nodes?.length || !graphData.edges?.length;

  // Responsive sidebar behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1100) {
        setIsSidebarOpen(true);   // su desktop sidebar sempre aperta
      } else {
        setIsSidebarOpen(false);  // su mobile parte chiusa
      }
    };

    handleResize(); // inizializza in base alla larghezza attuale
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (noGraphToShow) {
      return(
        <>
        <Header />
          <div className="riga headerTools">
            <h3 className="titolo titoloFeature">Graph</h3>
            <p className="text description">
              Graph is a powerful tool for visualizing semantic relationships between scientific objects, such as genes, proteins, diseases, and chemical compounds. Structuring data in an interactive and interpretable format facilitates pattern recognition, hypothesis generation, and knowledge discovery. Whether for literature analysis or bioinformatics research, Graph helps reveal hidden connections in complex datasets.
            </p>
            <div className="space"></div>
          </div>
          <center>
                <VizitStorageLoader
                  buttonClassName="sign-button button download-button"
                  buttonLabel="Upload from Vizit Storage"
                  textAction="Use in Graph"
                  isAdmin={isAdmin}
                  onLoad={(parsed) => {
                    if (location.pathname === '/graph') {
                      setJsonData(parsed);
                    } else {
                      navigate('/graph', {
                        state: { params: { pipelineData: parsed } },
                      });
                    }
                  }}
                />
              
              <div className="spaceGraph"></div>
              <div className="spaceGraph"></div>

              {isAdmin && (
                <>
                  <p>- or -</p>
                  <form className='uploadForm'>
                    <div className="file-upload-container">
                      <label htmlFor="fileInput" className="custom-file-label-upload textlink">
                        <AiOutlineCloudUpload className="upload-icon-35px textlink" />
                        {!file ? (
                          <span className="noBottomSpace">Upload a JSON file from your computer</span>
                        ) : (
                          <span className="noBottomSpace">{file.name}</span>
                        )}
                      </label>
                      <input
                        type="file"
                        id="fileInput"
                        accept=".json"
                        onChange={handleSubmit}
                        style={{ display: 'none' }}
                        required
                      />
                    </div>
                  </form>
                  <div className="spaceGraph"></div>
                </>
              )}
              
          </center>
        </>
      );
  }

  return (
    <>
      <Header />
      <div className="riga headerTools headerSidebarTools">
        <h3 className="titolo titoloFeature">GRAPH</h3>
        <span className="text description descriptionSidebarTools">
          Graph is a powerful tool for visualizing semantic relationships between scientific objects, such as genes, proteins, diseases, and chemical compounds. Structuring data in an interactive and interpretable format facilitates pattern recognition, hypothesis generation, and knowledge discovery. Whether for literature analysis or bioinformatics research, Graph helps reveal hidden connections in complex datasets.
        </span>
        <div className="space"></div>

        <button
          type="button"
          className="button sign-button sidebar-toggle"
          onClick={() => setIsSidebarOpen(prev => !prev)}
        >
          {isSidebarOpen ? 'Hide settings' : 'Show settings'}
        </button>
      </div>

      <div className="bodysidebar-layout">
        <div className={`ManageAreaSidebar ${isSidebarOpen ? 'is-open' : ''}`}>
          {/* --- QUI dentro incolli tutto quello che avevi prima in ManageAreaSidebar --- */}
          <div className="colonna4">
            <label className="textlink labelcss" htmlFor="scaleRange">Node Size: </label>
            <div className="spaceGraph"></div>
            <input
              type="range"
              id="scaleRange"
              min="1"
              max="50"
              defaultValue="10"
              onInput={(e) => updateNodeSize(network, e.target.value)}
            />
            <div className="spaceGraph"></div>
          </div>
          <div className="colonna4">
            <label className="textlink labelcss" htmlFor="labelSizeRange">Label Size: </label>
            <div className="spaceGraph"></div>
            <input
              type="range"
              id="labelSizeRange"
              min="10"
              max="50"
              defaultValue="18"
              onInput={(e) => updateLabelSize(network, e.target.value)}
            />
            <div className="spaceGraph"></div>
          </div>
          <div className="colonna4">
            <label className="textlink labelcss" htmlFor="distanceRange">Similarity Threshold: </label>
            <div className="spaceGraph"></div>
            <input
              type="range"
              id="distanceRange"
              min="0"
              max="10"
              step="0.01"
              defaultValue="0"
              onInput={(e) => {
                const similarityValue = e.target.value;
                const span = document.getElementById('similarityValue');
                if (span) span.textContent = similarityValue;
                filterGraph(
                  network,
                  document.getElementById('nameFilter')?.value ?? '',
                  parseFloat(similarityValue)
                );
              }}
            />
            <span id="similarityValue">0.00</span>
            <div className="spaceGraph"></div>
          </div>
          <div className="colonna4">
            <label className="textlink labelcss" htmlFor="nameFilter">Filter Nodes by Name: </label>
            <input
              type="text"
              id="nameFilter"
              placeholder="Enter node name..."
              onInput={(e) => filterGraph(
                network,
                e.target.value,
                parseFloat(document.getElementById('distanceRange')?.value ?? '0')
              )}
            />
            <div className="spaceGraph"></div>
          </div>
          <div className="colonna4">
            <label className="textlink labelcss">Export Visible Nodes</label>
            <button
              className="export-button sign-button button"
              disabled={!network}
              onClick={() => exportVisibleNodes(network)}
            >
              Export Visible Nodes
            </button>
          </div>
          <div className="riga">
            <Link
              className="textlink textLinkSign"
              to="/dashboard"
            >
              Back to Dashboard{' '}
              <FaArrowRight
                size={12}
                style={{ marginLeft: '8px', marginBottom: '-1px' }}
              />
            </Link>
          </div>
        </div>

        <div className="graph-body-sidebar">
          <div className="placeholder-graph">
            Hold CTRL and click on a node to select multiple nodes
          </div>

          {/* Explicit canvas height */}
          <div
            ref={containerRef}
            className="graph"
            id="mynetwork"
          />

          <div className="context-menu">
            <div
              className="menu-item export-button button"
              onClick={() => {
                if (selectedEdgeCount === 1 && selectedEdgeId && network) {
                  showEdgeDetails(network, selectedEdgeId);
                }
              }}
              style={{ opacity: selectedEdgeCount === 1 ? 1 : 0.5, pointerEvents: selectedEdgeCount === 1 ? 'auto' : 'none' }}
            >
              Show Edge Details
            </div>

            <div
              className="menu-item export-button button"
              onClick={selectedCount > 0 ? handleExpandSelected : undefined}
              style={{ opacity: selectedCount > 0 ? 1 : 0.5, pointerEvents: selectedCount > 0 ? 'auto' : 'none' }}
            >
              Expand Node(s)
            </div>

            <div
              className="menu-item export-button button"
              onClick={selectedCount > 0 ? hideSelectedNodes : undefined}
              style={{ opacity: selectedCount > 0 ? 1 : 0.5, pointerEvents: selectedCount > 0 ? 'auto' : 'none' }}
            >
              Hidden Node(s)
            </div>

            <div
              className="menu-item export-button button"
              onClick={selectedCount > 0 ? handleDownloadSelected : undefined}
              style={{ opacity: selectedCount > 0 ? 1 : 0.5, pointerEvents: selectedCount > 0 ? 'auto' : 'none' }}
            >
              Download Selected Nodes
            </div>

            <div
              className="menu-item export-button button"
              onClick={handleSelectAllNodes}
              style={{ opacity: network ? 1 : 0.5, pointerEvents: network ? 'auto' : 'none' }}
            >
              Select All Nodes
            </div>
          </div>
        </div>
        <div className='spaceGraph'></div>
      </div>
    </>
  );
};

export default GraphComponent;
